import { AlertTriangle, Lock, Unlock, Webhook } from "lucide-react";
import { Suspense, use, useState } from "react";
import { Navigate, useParams } from "react-router-dom";

import { AvailabilityBadge } from "@components/AvailabilityBadge.tsx";
import { MethodChip } from "@components/MethodChip.tsx";
import { ParamTable } from "@components/ParamTable.tsx";
import { Prose } from "@components/Prose.tsx";
import { SchemaTree } from "@components/SchemaTree.tsx";
import { SnippetPanel } from "@components/SnippetPanel.tsx";
import { cn } from "@lib/cn.ts";
import {
  deref,
  findOperation,
  findService,
  getBundle,
  getSpec,
  loadSnippets,
  loadSpec,
} from "@lib/content.ts";
import type {
  Json,
  MediaTypeObject,
  ParameterObject,
  ResponseObject,
  ServiceEntry,
} from "@lib/types.ts";

export function OperationPage() {
  const { version = "", operationId = "" } = useParams();

  return (
    // The specification is fetched on demand -- it is far too large to sit in
    // the initial bundle, and only these pages need it.
    <Suspense fallback={<PageSkeleton />}>
      <OperationDetail version={version} operationId={operationId} />
    </Suspense>
  );
}

function OperationDetail({
  version,
  operationId,
}: {
  version: string;
  operationId: string;
}) {
  use(loadSpec(version));
  const entry = findOperation(version, operationId);

  if (!entry) return <Navigate to={`/${version}`} replace />;

  const service = findService(version, entry.serviceId);
  const { availability } = getBundle(version);
  const op = entry.operation;

  const parameters = ((op.parameters as unknown[] | undefined) ?? [])
    .map((p) => deref(version, p) as ParameterObject | undefined)
    .filter((p): p is ParameterObject => Boolean(p));

  const requestBody = deref(version, op.requestBody);
  const requestMedia = (
    requestBody?.content as Record<string, MediaTypeObject> | undefined
  )?.["application/json"];

  const responses = (op.responses ?? {}) as Record<string, unknown>;
  const security = (op.security as Json[] | undefined) ?? [];
  const schemeNames = security.flatMap((s) => Object.keys(s));

  return (
    <div className="flex flex-col gap-8 px-4 py-7 sm:px-7 xl:flex-row xl:gap-10">
      {/* Details ------------------------------------------------------- */}
      <article className="min-w-0 flex-1 xl:max-w-[46rem]">
        <div className="mb-1.5 flex flex-wrap items-center gap-2">
          <MethodChip method={entry.method} size="md" />
          <code className="break-all font-mono text-[12.5px] text-muted">
            {entry.path}
          </code>
          {entry.isWebhook ? (
            <span className="inline-flex items-center gap-1 rounded-sm2 bg-info-tint px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-info">
              <Webhook className="size-3" aria-hidden />
              you implement this
            </span>
          ) : null}
        </div>

        <h1 className="text-[26px] font-semibold leading-tight tracking-tight text-ink">
          {entry.summary}
        </h1>

        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          {!entry.isWebhook ? (
            <AvailabilityBadge states={availability[entry.id]} />
          ) : null}
          <SecurityChip schemeNames={schemeNames} />
        </div>

        {entry.deprecated ? (
          <p
            role="alert"
            className="mt-4 flex items-start gap-2 rounded-default border border-warning/30 bg-warning-tint px-3 py-2 text-[13px] text-warning"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            This operation is deprecated and will be removed in a future
            version.
          </p>
        ) : null}

        <Prose html={entry.descriptionHtml} className="mt-5" />

        {schemeNames.length > 0 ? (
          <AuthSection version={version} schemeNames={schemeNames} />
        ) : null}

        <ParamTable
          version={version}
          title="Path parameters"
          parameters={parameters.filter((p) => p.in === "path")}
        />
        <ParamTable
          version={version}
          title="Query parameters"
          parameters={parameters.filter((p) => p.in === "query")}
        />
        <ParamTable
          version={version}
          title="Header parameters"
          parameters={parameters.filter((p) => p.in === "header")}
        />

        {requestMedia?.schema ? (
          <section className="mt-7">
            <h2 className="mb-1 text-[15px] font-semibold text-ink">
              Request body
            </h2>
            <p className="mb-2 text-[12.5px] text-subtle">
              <code>application/json</code>
              {requestBody?.required === true ? " · required" : " · optional"}
            </p>
            <Prose size="sm" html={requestBody?.descriptionHtml as string} />
            <div className="rounded-default border border-line px-3">
              <SchemaTree version={version} schema={requestMedia.schema} />
            </div>
          </section>
        ) : null}

        <ResponsesSection version={version} responses={responses} />
      </article>

      {/* Snippets ------------------------------------------------------ */}
      {service && !entry.isWebhook ? (
        <aside className="w-full shrink-0 xl:w-[var(--gt-code-w)]">
          <div className="xl:sticky xl:top-[calc(var(--gt-header-h)+1.75rem)]">
            <Suspense fallback={<SnippetSkeleton />}>
              <LazySnippets
                version={version}
                serviceId={entry.serviceId}
                operationId={entry.id}
                service={service}
                method={entry.method}
                path={entry.path}
                statuses={Object.keys(responses)}
              />
            </Suspense>
          </div>
        </aside>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function LazySnippets({
  version,
  serviceId,
  operationId,
  service,
  method,
  path,
  statuses,
}: {
  version: string;
  serviceId: string;
  operationId: string;
  service: ServiceEntry;
  method: string;
  path: string;
  statuses: string[];
}) {
  const bundle = use(loadSnippets(version, serviceId));

  const responseExamples = statuses
    .filter((status) => bundle.examples[`${operationId}:${status}`])
    .sort()
    .slice(0, 3)
    .map((status) => ({
      status,
      lines: bundle.examples[`${operationId}:${status}`],
    }));

  return (
    <SnippetPanel
      service={service}
      method={method}
      path={path}
      snippets={bundle.snippets[operationId]}
      responseExamples={responseExamples}
    />
  );
}

function PageSkeleton() {
  return (
    <div aria-hidden className="px-4 py-7 sm:px-7">
      <div className="max-w-[46rem] space-y-4">
        <div className="h-5 w-40 animate-pulse rounded-sm2 bg-surface-2" />
        <div className="h-8 w-2/3 animate-pulse rounded-sm2 bg-surface-2" />
        <div className="h-4 w-full animate-pulse rounded-sm2 bg-surface-2" />
        <div className="h-4 w-5/6 animate-pulse rounded-sm2 bg-surface-2" />
        <div className="h-56 animate-pulse rounded-default bg-surface-2" />
      </div>
    </div>
  );
}

function SnippetSkeleton() {
  return (
    <div
      aria-hidden
      className="space-y-3 rounded-default border border-code-line bg-code-bg p-3"
    >
      <div className="h-5 w-28 animate-pulse rounded-sm2 bg-code-bg-3" />
      <div className="h-40 animate-pulse rounded-sm2 bg-code-bg-2" />
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function SecurityChip({ schemeNames }: { schemeNames: string[] }) {
  const open = schemeNames.length === 0;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-sm2 px-2 py-0.5 text-[11.5px] font-medium",
        open ? "bg-surface-2 text-muted" : "bg-accent-tint text-accent",
      )}
    >
      {open ? (
        <>
          <Unlock className="size-3.5" aria-hidden />
          No authentication
        </>
      ) : (
        <>
          <Lock className="size-3.5" aria-hidden />
          Requires a token
        </>
      )}
    </span>
  );
}

function AuthSection({
  version,
  schemeNames,
}: {
  version: string;
  schemeNames: string[];
}) {
  const schemes = (getSpec(version).components.securitySchemes ?? {}) as Json;

  return (
    <section className="mt-7">
      <h2 className="mb-2 text-[15px] font-semibold text-ink">
        Authentication
      </h2>
      <div className="space-y-3">
        {schemeNames.map((name) => {
          const scheme = schemes[name] as Json | undefined;
          if (!scheme) return null;
          return (
            <div
              key={name}
              className="rounded-default border border-line px-3 py-2.5"
            >
              <div className="flex flex-wrap items-baseline gap-x-2">
                <code className="font-mono text-[13px] font-semibold text-ink">
                  {name}
                </code>
                <span className="font-mono text-[11.5px] text-subtle">
                  {scheme.type === "http"
                    ? `${String(scheme.scheme)} · ${String(scheme.bearerFormat ?? "")}`
                    : `${String(scheme.type)} · ${String(scheme.in)} "${String(scheme.name)}"`}
                </span>
              </div>
              <Prose
                size="sm"
                html={scheme.descriptionHtml as string}
                className="mt-1 text-muted"
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ResponsesSection({
  version,
  responses,
}: {
  version: string;
  responses: Record<string, unknown>;
}) {
  const statuses = Object.keys(responses).sort();
  const [active, setActive] = useState(statuses[0] ?? "");

  if (statuses.length === 0) return null;

  const response = deref(version, responses[active]) as
    ResponseObject | undefined;
  const media = response?.content?.["application/json"];
  const headers = response?.headers;

  return (
    <section className="mt-7">
      <h2 className="mb-2 text-[15px] font-semibold text-ink">Responses</h2>

      <div
        role="tablist"
        aria-label="Response status"
        className="mb-3 flex flex-wrap gap-1"
      >
        {statuses.map((status) => (
          <button
            key={status}
            type="button"
            role="tab"
            aria-selected={status === active}
            onClick={() => setActive(status)}
            className={cn(
              "rounded-sm2 border px-2 py-0.5 font-mono text-[12px] transition",
              status === active
                ? "border-accent bg-accent-tint text-accent"
                : "border-line text-muted hover:text-ink",
              status.startsWith("2") && status !== active && "text-success",
              (status.startsWith("4") || status.startsWith("5")) &&
                status !== active &&
                "text-danger",
            )}
          >
            {status}
          </button>
        ))}
      </div>

      <div className="rounded-default border border-line px-3 py-2.5">
        <Prose
          size="sm"
          html={response?.descriptionHtml}
          className="text-muted"
        />

        {headers && Object.keys(headers).length > 0 ? (
          <div className="mt-3">
            <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-subtle">
              Response headers
            </h3>
            {Object.entries(headers).map(([name, headerRaw]) => {
              const header = deref(version, headerRaw);
              return (
                <div key={name} className="text-[12.5px]">
                  <code className="font-semibold text-ink">{name}</code>
                  <span className="ml-2 text-muted">
                    {(header?.description as string) ?? ""}
                  </span>
                </div>
              );
            })}
          </div>
        ) : null}

        {media?.schema ? (
          <div className="mt-2">
            <SchemaTree version={version} schema={media.schema} />
          </div>
        ) : (
          <p className="mt-2 text-[12.5px] italic text-subtle">
            No response body.
          </p>
        )}
      </div>
    </section>
  );
}
